import React from 'react';
import { Area, AreaChart, Brush, CartesianGrid, Label, Tooltip, XAxis, YAxis, ComposedChart, ReferenceArea } from 'recharts';
import i18next from 'i18next';

import CustomTooltip from './custom_tooltip';
import GraphUtility from './graph_utility';

// Read data and turn it into a start-end format of open/closed intervals
function processDataToIntervals(data) {
  return data.map(entry => ({
      state: entry.state,
      timeInterval: [entry.startTimestamp, entry.endTimestamp],
      duration: entry.endTimestamp - entry.startTimestamp
    })
  );
}

// Generate a proportional number of synthetic datapoints for each interval
function generateSyntheticData(intervalData) {
  // Sum up all durations to obtain total time being viewed
  let totalTime = 0;
  for (let i = 0; i < intervalData.length; i++) {
    totalTime += intervalData[i].duration;
  }

  // Generate X datapoints for each interval, where X is proportional to the duration of the interval
  let data = [];
  for (let i = 0; i < intervalData.length; i++) {
    const numPoints = Math.ceil(100 * intervalData[i].duration / totalTime);
    const offset = Math.round(intervalData[i].duration / numPoints);
    for (let j = 0; j < numPoints; j++) {
      const isOpen = intervalData[i].state === "Open" || intervalData[i].state === "true";
      const isClosed = intervalData[i].state === "Closed" || intervalData[i].state === "false";

      data.push({
        timestamp: intervalData[i].timeInterval[0] + (j * offset), 
        state: isOpen ? 2 : (isClosed ? -2 : null)
      });
    }
  }
  return data;
}


class DoorAreaGraph extends React.Component {
  constructor(props) {
    super(props);

    this.BRUSH_GRAPH_HEIGHT = 100;
    this.BRUSH_GRAPH_X_HEIGHT = 50;
    this.BRUSH_HEIGHT = this.BRUSH_GRAPH_HEIGHT - this.BRUSH_GRAPH_X_HEIGHT;

    const intervalData = processDataToIntervals(this.props.graphVitals.data);
    const data = generateSyntheticData(intervalData);
    const dataLength = data.length;
    this.state = {
      startIndex: 0, 
      endIndex: dataLength - 1,
      startTime: data[0].timestamp,
      endTime: data[data.length - 1].timestamp,
      data: data
    }

    // Bind class methods
    this.gradientOffset = this.gradientOffset.bind(this);
    this.renderBrush = this.renderBrush.bind(this);
    this.renderGradient = this.renderGradient.bind(this);
    this.formatXTicks = this.formatXTicks.bind(this);
    this.formatYTicks = this.formatYTicks.bind(this);
    this.calculateTicks = this.calculateTicks.bind(this);
  } 

  renderBrush() {
    const data = this.state.data;
    const firstTime = this.state.startTime;
    const lastTime = this.state.endTime;
    let fill1 = ''.concat('splitColorBrush', this.props.id);
    let fill2 = ''.concat('url(#', fill1, ')');
    return (
      <div className="chart-brush-container">
        <div>
          <AreaChart width={this.props.width} height={this.BRUSH_GRAPH_HEIGHT} data={data}>
            <XAxis type="number" dataKey="timestamp" scale="time" ticks={this.calculateTicks({brush: true})} domain={[firstTime, lastTime]} tickFormatter={(tick) => this.formatXTicks(tick, true)} height={this.BRUSH_GRAPH_X_HEIGHT}>
              <Label value={`${i18next.t('graphing.start')}, ${i18next.t('graphing.end')}`} offset={10} position="insideBottom"/>
            </XAxis>
            <YAxis type="number" allowDecimals={false} domain={[-2,2]} dataKey="state" tickFormatter={this.formatYTicks} interval={0}/>
            {this.state.data ? this.renderGradient({brush: true, fill: fill1}) : ""}
            <Area type="step" dataKey="state" stroke="none" fill={fill2} isAnimationActive={false}/>
          </AreaChart>
        </div>
        <div className="chart-brush">
          <ComposedChart width={this.props.width} height={this.BRUSH_HEIGHT} data={data}>
            <XAxis type="number" dataKey="timestamp" scale="time" domain={[firstTime, lastTime]} height={-60}/>
            <YAxis type="number" allowDecimals={false} domain={[-2,2]} dataKey="state" tickFormatter={this.formatYTicks} interval={0}/>
            <Brush height={this.BRUSH_HEIGHT} dataKey='timestamp' startIndex={this.state.startIndex} endIndex={this.state.endIndex} fill="rgba(0, 0, 0, 0)" stroke="gray" onChange={(args) => this.setState({startIndex: args.startIndex, endIndex: args.endIndex})} tickFormatter={(tick) => ""}/>
          </ComposedChart>
        </div>
      </div>
    );
  }

  gradientOffset(params) {
    const { brush } = params;
    const data = this.state.data;
    const startSlice = !brush ? this.state.startIndex : 0;
    const endSlice = !brush ? this.state.endIndex : data.length;

    const newData = data.slice(startSlice, endSlice + 1);
    const dataMax = Math.max(...newData.map((i) => i.state));
    const dataMin = Math.min(...newData.map((i) => i.state));

    if (dataMin === -2 && dataMax === 0) { // all closed, i.e. all green
      return 0;
    } else if (dataMin === 0 && dataMax === 2) { // all open, i.e. all red
      return 1;
    } else if (dataMin === -2 && dataMax === 2) { // open and closed, i.e. mixed color
      return 0.5;
    }
  }

  renderGradient(params) {
    const { brush, fill } = params;
    const offset = this.gradientOffset({brush: brush});
    return (
      <defs>
        <linearGradient id={fill} x1="0" y1="0" x2="0" y2="1">
          <stop offset={offset} stopColor="red" stopOpacity={1}/>
          <stop offset={offset} stopColor="green" stopOpacity={1}/>
        </linearGradient>
      </defs>
    );
  }

  renderOutages(outagesAtStartOrEnd, firstTime, lastTime) {
    return outagesAtStartOrEnd.map((outage, index) => (
      <ReferenceArea key={index} x1={outage.start > firstTime ? outage.start : firstTime} x2={outage.end < lastTime ? outage.end : lastTime} fill="yellow" fillOpacity={0.125}/>
    ));
  }

  calculateTicks(args) {
    const data = this.state.data;
    const brushStart = data[!args.brush ? this.state.startIndex : 0].timestamp;
    const brushEnd = data[!args.brush ? this.state.endIndex : data.length - 1].timestamp;
    return GraphUtility.calculateTicks(brushStart, brushEnd);
  }

  formatXTicks(tick, isBrush = false) {
    const data = this.state.data;
    const brushStart = data[isBrush ? 0 : this.state.startIndex].timestamp;
    const brushEnd = data[isBrush ? data.length - 1 : this.state.endIndex].timestamp;
    return GraphUtility.formatTicks(tick, brushStart, brushEnd);
  } 

  formatYTicks(tick) {
    if (tick === 1) {
      return i18next.t('magnetometer-data-string-display.Open');
    } else if (tick === -1) {
      return i18next.t('magnetometer-data-string-display.Closed');
    } else {
      return "";
    }
  } 

  render() {
    const data = this.state.data.slice(this.state.startIndex, this.state.endIndex + 1);
    const outagesAtStartOrEnd = this.props.graphVitals.outagesAtStartOrEnd;
    const firstTime = this.state.data[this.state.startIndex].timestamp;
    const lastTime = this.state.data[this.state.endIndex].timestamp;
    let chartHeight = this.props.showBrush ? this.props.height - this.BRUSH_GRAPH_HEIGHT : this.props.height;
    let fill1 = `splitColor${this.props.id}`;
    let fill2 = `url(#${fill1})`;

    return (
      <div style={{marginLeft: "-30px"}}>
        <AreaChart width={this.props.width} height={chartHeight} data={data}>
          <CartesianGrid strokeDasharray="0 0"/>
          <Tooltip content={<CustomTooltip graphType="door" external={window.external} />}/>
          <XAxis type="number" dataKey="timestamp" scale="time" ticks={this.calculateTicks({brush: false})} domain={[firstTime, lastTime]} tickFormatter={this.formatXTicks} height={50}/>
          <YAxis type="number" allowDecimals={false} domain={[-2,2]} dataKey="state" tickFormatter={this.formatYTicks} interval={0}/>
          {this.state.data ? this.renderGradient({brush: false, fill: fill1}) : ""}
          <Area type="step" dataKey="state" stroke="none" fill={fill2} isAnimationActive={false}/>
          {outagesAtStartOrEnd && outagesAtStartOrEnd.length > 0 ? this.renderOutages(outagesAtStartOrEnd, firstTime, lastTime) : null} 
        </AreaChart>

        {this.props.showBrush ? this.renderBrush() : null }
      </div>

    );
  } 
} 

export default DoorAreaGraph;
